import React, { useState } from "react";
import { useTags } from "@/features/tags/hooks/useTags";
import { parseKeyValue, tagSchema } from "@/features/tags/utils/tag-utils";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "../components/ui/button-new";
import IconButton from "@/components/ui/icon-button";
import { Input } from "../components/ui/input";
import { AutocompleteInput } from "@/components/ui/autocomplete-input";
import { cn } from "@/lib/utils";
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "../components/ui/form";

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogClose,
} from "../components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Trash2, Plus, RefreshCw, Loader2, Tag, Pencil } from "lucide-react";
import { usePageFab } from "@/hooks/use-page-fab";
import { H1HeaderCard } from "@/components/layout/h1-header-card";

type TagFormValues = z.infer<typeof tagSchema>;

export function TagsPage() {
    const { tagsByKey, createTag, updateTag, removeTag, refreshTags, loading } =
        useTags();
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [deletingTags, setDeletingTags] = useState<Record<string, boolean>>(
        {},
    );
    const [tagToDelete, setTagToDelete] = useState<{
        tagId: string;
        keyValue: string;
    } | null>(null);
    const [editingTag, setEditingTag] = useState<{
        tagId: string;
        key: string;
        value: string;
    } | null>(null);

    // Format keys for autocomplete
    const keyItems = React.useMemo(() => {
        return Object.keys(tagsByKey).map((key) => ({
            value: key,
            label: key,
        }));
    }, [tagsByKey]);

    // Setup form with zod validation
    const form = useForm<TagFormValues>({
        resolver: zodResolver(tagSchema),
        defaultValues: {
            key: "",
            value: "",
        },
    });

    // Reset form state
    const resetForm = React.useCallback(() => {
        form.reset();
    }, [form]);

    // Define the FAB for this page with useCallback to maintain reference stability
    const handleFabClick = React.useCallback(() => {
        resetForm();
        setIsCreateModalOpen(true);
    }, [resetForm]);

    // Use a stable action object
    const fabAction = React.useMemo(
        () => ({
            icon: Plus,
            label: "Create Tag",
            onClick: handleFabClick,
            color: "primary" as const,
        }),
        [handleFabClick],
    );

    usePageFab(fabAction);

    const handleCreateTag = async (values: TagFormValues) => {
        setIsCreating(true);
        try {
            await createTag(values.key, values.value);
            resetForm();
            setIsCreateModalOpen(false);
        } catch (error) {
            console.error("Failed to create tag:", error);
        } finally {
            setIsCreating(false);
        }
    };

    const openEditModal = (tagId: string, keyValue: string) => {
        const parsed = parseKeyValue(keyValue);
        if (parsed) {
            setEditingTag({ tagId, key: parsed.key, value: parsed.value });
            form.reset({ key: parsed.key, value: parsed.value });
            setIsEditModalOpen(true);
        }
    };

    const handleUpdateTag = async (values: TagFormValues) => {
        if (!editingTag) return;
        setIsUpdating(true);
        try {
            await updateTag(editingTag.tagId, values.key, values.value);
            resetForm();
            setIsEditModalOpen(false);
            setEditingTag(null);
        } catch (error) {
            console.error("Failed to update tag:", error);
        } finally {
            setIsUpdating(false);
        }
    };

    const confirmDeleteTag = (tagId: string, keyValue: string) => {
        setTagToDelete({ tagId, keyValue });
    };

    const handleDeleteTag = async (tagId: string) => {
        setDeletingTags((prev) => ({ ...prev, [tagId]: true }));
        try {
            await removeTag(tagId);
            setTagToDelete(null);
        } catch (error) {
            console.error("Failed to delete tag:", error);
        } finally {
            setDeletingTags((prev) => ({ ...prev, [tagId]: false }));
        }
    };

    return (
        <div className="max-w-xl mx-auto">
            {/* Create new tag modal - shown when FAB is clicked */}
            <Dialog
                open={isCreateModalOpen}
                onOpenChange={(open) => {
                    if (!open) resetForm();
                    setIsCreateModalOpen(open);
                }}
            >
                <DialogContent className="sm:max-w-[425px] p-6">
                    <DialogHeader>
                        <DialogTitle>Create New Tag</DialogTitle>
                        {/* <div className="flex items-center gap-2 mt-1">
                            <AlertTriangle className="h-4 w-4 text-amber-500" />
                            <p className="text-sm">
                                Tags cannot be modified after creation.
                            </p>
                            <AlertTriangle className="h-4 w-4 text-amber-500" />
                        </div> */}
                    </DialogHeader>
                    <Form {...form}>
                        <form
                            onSubmit={form.handleSubmit(handleCreateTag)}
                            className="space-y-4"
                        >
                            <FormField
                                control={form.control}
                                name="key"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Key</FormLabel>
                                        <FormControl>
                                            <AutocompleteInput
                                                value={field.value}
                                                onChange={field.onChange}
                                                items={keyItems}
                                                placeholder="Type to search or create a key"
                                                autoFocus
                                                showAbove={true}
                                            />
                                        </FormControl>
                                        <FormDescription>
                                            The group or category this tag
                                            belongs to.
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="value"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Value</FormLabel>
                                        <FormControl>
                                            <Input placeholder="" {...field} />
                                        </FormControl>
                                        <FormDescription>
                                            The specific item within this key
                                            (e.g., "Website" under "Project").
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <DialogFooter className="mt-6">
                                <DialogClose asChild>
                                    <Button type="button" variant="outline">
                                        Cancel
                                    </Button>
                                </DialogClose>
                                <Button type="submit" loading={isCreating}>
                                    Create
                                </Button>
                            </DialogFooter>
                        </form>
                    </Form>
                </DialogContent>
            </Dialog>

            {/* Edit tag modal */}
            <Dialog
                open={isEditModalOpen}
                onOpenChange={(open) => {
                    if (!open) {
                        resetForm();
                        setEditingTag(null);
                    }
                    setIsEditModalOpen(open);
                }}
            >
                <DialogContent className="sm:max-w-[425px] p-6">
                    <DialogHeader>
                        <DialogTitle>Edit Tag</DialogTitle>
                    </DialogHeader>
                    <Form {...form}>
                        <form
                            onSubmit={form.handleSubmit(handleUpdateTag)}
                            className="space-y-4"
                        >
                            <FormField
                                control={form.control}
                                name="key"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Key</FormLabel>
                                        <FormControl>
                                            <AutocompleteInput
                                                value={field.value}
                                                onChange={field.onChange}
                                                items={keyItems}
                                                placeholder="Type to search or create a key"
                                                autoFocus
                                                showAbove={true}
                                            />
                                        </FormControl>
                                        <FormDescription>
                                            The group or category this tag
                                            belongs to.
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="value"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Value</FormLabel>
                                        <FormControl>
                                            <Input placeholder="" {...field} />
                                        </FormControl>
                                        <FormDescription>
                                            The specific item within this key
                                            (e.g., "Website" under "Project").
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <DialogFooter className="mt-6">
                                <DialogClose asChild>
                                    <Button type="button" variant="outline">
                                        Cancel
                                    </Button>
                                </DialogClose>
                                <Button type="submit" loading={isUpdating}>
                                    Update
                                </Button>
                            </DialogFooter>
                        </form>
                    </Form>
                </DialogContent>
            </Dialog>

            <H1HeaderCard title="Tags" className="mb-14">
                <p>Tags help you organize receipts into custom categories.</p>
                <p>&nbsp;</p>
                <p>
                    Each tag has a key (the category name like "Project") and a
                    value (the specific item like "Website").
                </p>
            </H1HeaderCard>

            <div className="flex items-end mb-4 justify-end">
                <IconButton
                    onClick={refreshTags}
                    disabled={loading}
                    size="sm"
                    color="secondary"
                    variant="outlined"
                    className="flex-shrink-0"
                >
                    <RefreshCw
                        className={cn("h-4 w-4", loading && "animate-spin")}
                    />
                </IconButton>
            </div>

            {loading && Object.keys(tagsByKey).length === 0 && (
                <div className="flex items-center justify-center py-12">
                    <div className="flex items-center gap-2 text-md-on-surface-variant">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-sm">Loading tags...</span>
                    </div>
                </div>
            )}

            {Object.keys(tagsByKey).length === 0 && !loading && (
                <div className="text-center py-12">
                    <div className="p-3 bg-md-surface-variant rounded-full w-fit mx-auto mb-3">
                        <Tag className="h-6 w-6 text-md-on-surface-variant" />
                    </div>
                    <p className="text-md-on-surface-variant text-sm">
                        No tags found
                    </p>
                    <p className="text-md-on-surface-variant text-xs mt-1">
                        Create your first tag to get started
                    </p>
                </div>
            )}

            {/* Tag groups */}
            {Object.keys(tagsByKey).map((key) => (
                <div key={key} className="mb-16">
                    <h2 className="text-2xl sm:text-3xl font-medium leading-tight text-md-on-surface mb-4 px-1">
                        {key}
                    </h2>
                    <div className="bg-md-surface-level-1 rounded-xl overflow-hidden -shadow-xs">
                        {tagsByKey[key].map((tag, index) => {
                            const parts = parseKeyValue(tag.keyValue);
                            if (!parts) return null;

                            const isLast = index === tagsByKey[key].length - 1;

                            return (
                                <div
                                    key={tag.keyValue}
                                    className={`flex items-center px-4 py-3 ${!isLast ? "border-b border-md-outline-variant" : ""}`}
                                >
                                    <div className="flex-1 flex items-center pl-2">
                                        {/* <div className="h-2 w-2 rounded-full bg-md-primary mr-3 opacity-80"></div> */}
                                        <span className="text-md-on-surface">
                                            {parts.value}
                                        </span>
                                    </div>

                                    <div className="flex items-center gap-1">
                                        <Button
                                            onClick={() =>
                                                openEditModal(
                                                    tag.tagId,
                                                    tag.keyValue,
                                                )
                                            }
                                            size="icon"
                                            variant="ghost"
                                            className="text-md-on-surface-variant hover:text-md-primary hover:bg-md-primary-container/10"
                                        >
                                            <Pencil className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            onClick={() =>
                                                confirmDeleteTag(
                                                    tag.tagId,
                                                    tag.keyValue,
                                                )
                                            }
                                            size="icon"
                                            variant="ghost"
                                            className="text-md-on-surface-variant hover:text-md-error hover:bg-md-error-container/10"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}

            {/* Delete confirmation dialog */}
            <AlertDialog
                open={!!tagToDelete}
                onOpenChange={(open) => !open && setTagToDelete(null)}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Tag</AlertDialogTitle>
                        <AlertDialogDescription>
                            {tagToDelete &&
                                (() => {
                                    const parts = parseKeyValue(
                                        tagToDelete.keyValue,
                                    );
                                    if (parts) {
                                        return (
                                            <div className="mb-4 p-3 border border-amber-200 bg-amber-50 rounded-md">
                                                <div>
                                                    <strong>Key:</strong>{" "}
                                                    {parts.key}
                                                </div>
                                                <div>
                                                    <strong>Value:</strong>{" "}
                                                    {parts.value}
                                                </div>
                                            </div>
                                        );
                                    }
                                    return null;
                                })()}
                            <p className="mb-2">
                                Are you sure you want to delete this tag?
                            </p>
                            <ul className="list-disc pl-5 space-y-1 text-sm">
                                <li>
                                    You won't be able to tag new receipts with
                                    this tag
                                </li>
                                <li>
                                    You won't be able to search for receipts
                                    using this tag
                                </li>
                                <li>
                                    Existing receipts with this tag will remain
                                    tagged until manually changed
                                </li>
                                <li>
                                    If you add the same tag again later, you'll
                                    be able to search for it again
                                </li>
                            </ul>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() =>
                                tagToDelete &&
                                handleDeleteTag(tagToDelete.tagId)
                            }
                            className="bg-red-600 hover:bg-red-700 text-white"
                            disabled={
                                tagToDelete
                                    ? deletingTags[tagToDelete.tagId]
                                    : false
                            }
                        >
                            {tagToDelete && deletingTags[tagToDelete.tagId] ? (
                                <>
                                    <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
                                    Deleting...
                                </>
                            ) : (
                                "Delete"
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
